import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { FlowStage } from '@/types/psiFlow';

export interface StageNodeData extends Record<string, unknown> {
  stage: FlowStage;
  label: string;
  docNumber?: string;
  status?: string;
  amount?: number;
  currency?: string;
  date?: string;
  isOverdue?: boolean;
  hasDocument?: boolean;
  isCompleted?: boolean;
  isOptional?: boolean;
  onOpen?: () => void;
  onCreate?: () => void;
  onClick?: () => void;
}

const stageIcons: Record<FlowStage, string> = {
  purchase_order: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
  inbound: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  inventory: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  invoice: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  quotation: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  sales_order: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
  delivery: 'M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0',
  return: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6',
};

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  ordered: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  processing: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300',
  shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  delivered: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  received: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  pending_iqc: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  iqc_passed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  iqc_failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

const PSIFlowStageNode: React.FC<{ data: StageNodeData }> = ({ data }) => {
  const {
    stage,
    label,
    docNumber,
    status,
    amount,
    currency,
    date,
    isOverdue,
    hasDocument,
    isCompleted,
    isOptional,
    onOpen,
    onCreate,
    onClick,
  } = data;

  const iconPath = stageIcons[stage] || stageIcons.invoice;

  // Border style
  let borderClass = 'border-2 border-dashed border-gray-300 dark:border-gray-600';
  if (hasDocument) {
    if (isOverdue) {
      borderClass = 'border-2 border-red-500 animate-pulse';
    } else if (isCompleted) {
      borderClass = 'border-2 border-green-500';
    } else {
      borderClass = 'border-2 border-blue-500';
    }
  }
  if (isOptional && !hasDocument) {
    borderClass = 'border-2 border-dashed border-gray-400 dark:border-gray-500';
  }

  // Background
  let bgClass = 'bg-white dark:bg-gray-800';
  if (isCompleted && hasDocument) {
    bgClass = 'bg-green-50 dark:bg-green-900/20';
  }

  const formatAmount = (amt: number, cur: string) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: cur || 'TWD',
      minimumFractionDigits: 0,
    }).format(amt);
  };

  return (
    <div className={`group relative w-[240px] rounded-lg shadow-sm ${borderClass} ${bgClass} p-3`}>
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 !bg-gray-400 !border-2 !border-white dark:!border-gray-800"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 !bg-gray-400 !border-2 !border-white dark:!border-gray-800"
      />

      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded ${hasDocument ? 'bg-blue-100 dark:bg-blue-900' : 'bg-gray-100 dark:bg-gray-700'}`}>
          <svg className={`w-4 h-4 ${hasDocument ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
          </svg>
        </div>
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{label}</span>
        {isOverdue && (
          <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )}
        {isCompleted && (
          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Content */}
      <div className="space-y-1">
        {hasDocument ? (
          <>
            <p className="text-xs font-mono text-gray-600 dark:text-gray-400">{docNumber}</p>
            {status && (
              <span className={`inline-block px-1.5 py-0.5 text-xs rounded ${statusColors[status] || statusColors.draft}`}>
                {status}
              </span>
            )}
            {amount !== undefined && amount > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatAmount(amount, currency || 'TWD')}
              </p>
            )}
            {date && (
              <p className="text-xs text-gray-400 dark:text-gray-500">{date}</p>
            )}
          </>
        ) : (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">
            {isOptional ? 'Optional' : 'Not Created'}
          </p>
        )}
      </div>

      {/* Hover action */}
      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-white/80 dark:bg-gray-800/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
        {hasDocument && onClick && (
          <button
            onClick={onClick}
            className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
          >
            Details
          </button>
        )}
        {hasDocument && onOpen && (
          <button
            onClick={onOpen}
            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Open
          </button>
        )}
        {!hasDocument && onCreate && (
          <button
            onClick={onCreate}
            className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
          >
            + Create
          </button>
        )}
      </div>
    </div>
  );
};

export default memo(PSIFlowStageNode);
